import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setCurrentUser, selectCurrentUser } from "./userSlice";
import {
  Modal,
  ModalHeader,
  ModalBody,
  FormGroup,
  Label,
  Input,
  Button,
} from "reactstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import defaultAvatar from "../../app/assets/img/favicon.ico"; // Import default avatar image
import { validateUserLoginForm } from "../../utils/validateUserLoginForm";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from "firebase/auth";
import { auth } from "../../app/firebase.config";
import { isGoogleDriveConnectionEnabled } from "../../app/googleDriveFeature";
import LegalLinks from "../../components/LegalLinks";
import { setAnalyticsConsent as syncAnalyticsConsent } from "../../utils/productAnalytics";

const UserLoginForm = ({ showButton = true }) => {
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginError, setLoginError] = useState(null); // State to store login error
  const [isGoogleSignInStarting, setIsGoogleSignInStarting] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState(() => {
    const storedConsent = localStorage.getItem("teachleague_analytics_consent");
    return storedConsent === null ? true : storedConsent === "true";
  });
  const currentUser = useSelector(selectCurrentUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const showDriveAvailabilityNote = isGoogleDriveConnectionEnabled();

  useEffect(() => {
    if (currentUser && loginModalOpen) {
      setLoginModalOpen(false);
      setLoginError(null);
    }
  }, [currentUser, loginModalOpen]);

  useEffect(() => {
    const handleOpenLogin = () => {
      setLoginModalOpen(true);
    };

    window.addEventListener("open-login-modal", handleOpenLogin);
    return () => {
      window.removeEventListener("open-login-modal", handleOpenLogin);
    };
  }, []);

  useEffect(() => {
    syncAnalyticsConsent(analyticsConsent);
  }, [analyticsConsent]);

  const handleLogin = async (values) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        values.email,
        values.password
      );
      const user = userCredential.user;

      // Extract only serializable properties from Firebase User
      const userData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        emailVerified: user.emailVerified,
      };

      // Dispatch action to set current user
      dispatch(setCurrentUser(userData));
      setLoginModalOpen(false);
    } catch (error) {
      // Handle login error
      setLoginError(
        "No account was found with the credentials you provided. Try again or create a new account."
      );
      console.error("Login error:", error.message);
    }
  };

  const handleGoogleLogin = async () => {
    if (isGoogleSignInStarting) {
      return;
    }

    setIsGoogleSignInStarting(true);
    const hostname = window.location.hostname;
    const isLocalhost =
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "[::1]";
    const customRedirectHosts = new Set([
      "teachleague.com",
      "www.teachleague.com",
      "missingvoices.app",
      "www.missingvoices.app",
    ]);
    const isFirebaseCustomDomain = customRedirectHosts.has(hostname);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: "select_account",
    });

    try {
      localStorage.setItem("canvas_connect_prompt", "1");
      console.log("🔐 Starting Google sign-in", {
        flow: isLocalhost ? "popup" : "redirect",
        hostname,
      });

      if (isLocalhost) {
        const popupResult = await signInWithPopup(auth, provider);
        const user = popupResult?.user;
        if (!user) {
          throw new Error("Google sign-in completed without a user session.");
        }

        dispatch(
          setCurrentUser({
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            emailVerified: user.emailVerified,
            googleAccessToken: null,
            driveEnabled: false,
          })
        );
        setLoginError(null);
        setLoginModalOpen(false);
        console.log("✅ Google popup sign-in successful:", user.email);
        return;
      }

      if (isFirebaseCustomDomain) {
        const redirectNonce = String(Date.now());
        sessionStorage.setItem("firebase_redirect_nonce", redirectNonce);
        const redirectUrl = new URL(window.location.href);
        redirectUrl.searchParams.set("authRedirect", redirectNonce);
        window.history.replaceState({}, document.title, redirectUrl.toString());
      }

      await signInWithRedirect(auth, provider);
      // Note: The page will redirect, so code after this won't run
      // The result is handled by getRedirectResult in App.js
    } catch (error) {
      const code = error?.code || "";

      if (
        isLocalhost &&
        (code === "auth/popup-blocked" || code === "auth/web-storage-unsupported")
      ) {
        try {
          await signInWithRedirect(auth, provider);
          return;
        } catch (redirectError) {
          console.error(
            "Google redirect fallback failed:",
            redirectError?.code,
            redirectError?.message || redirectError
          );
        }
      }

      sessionStorage.removeItem("firebase_redirect_nonce");
      const rollbackUrl = new URL(window.location.href);
      if (rollbackUrl.searchParams.has("authRedirect")) {
        rollbackUrl.searchParams.delete("authRedirect");
        window.history.replaceState({}, document.title, rollbackUrl.toString());
      }
      localStorage.removeItem("canvas_connect_prompt");

      if (code === "auth/unauthorized-domain") {
        setLoginError("This domain is not authorized for Google sign-in yet.");
      } else if (code === "auth/popup-blocked") {
        setLoginError("Popup was blocked. Please allow popups and try again.");
      } else if (code === "auth/popup-closed-by-user") {
        setLoginError("Google sign-in was canceled before completion.");
      } else if (code === "auth/operation-not-allowed") {
        setLoginError("Google sign-in is not enabled in Firebase Authentication.");
      } else {
        setLoginError(
          "Google sign-in failed. Please try again or use email/password."
        );
      }

      console.error("Google login error:", code, error?.message || error);
    } finally {
      setIsGoogleSignInStarting(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Clear all tokens and session data
      localStorage.removeItem('google_access_token');
      localStorage.removeItem('google_access_token_timestamp');
      sessionStorage.clear();

      // Sign out from Firebase
      await signOut(auth);

      // Clear Redux state (will be handled by onAuthStateChanged listener in App.js)
      console.log('🚪 User logged out successfully');
    } catch (error) {
      console.error("Logout error:", error.message);
    }
  };

  const handleAvatarClick = () => {
    setLoginModalOpen(false);
    navigate("/mydata");
  };

  const loginUnlockSummary =
    "Sign in to save your gradebook, connect Canvas and Drive, and keep lesson-planning context across Teach League.";

  return (
    <>
      {showButton && (
        <span className="navbar-text ml-auto">
          {currentUser ? (
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="avatar p-0 border-0 bg-transparent"
                onClick={handleAvatarClick}
                style={{ cursor: "pointer" }}
                aria-label="View account"
              >
                <img
                  src={currentUser.photoURL || defaultAvatar} // Use Google profile photo if available
                  alt={"user"}
                  style={{ width: "100%", height: "100%" }}
                />
              </button>
              <Button
                onClick={handleLogout}
                color="danger"
                size="sm"
                style={{
                  padding: '0.375rem 0.75rem',
                  fontSize: '0.875rem'
                }}
              >
                <i className="fa fa-sign-out" /> Logout
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => setLoginModalOpen(true)}
              color="primary"
              className="login-btn"
            >
              <i className="fa fa-sign-in fa-lg" /> Login
            </Button>
          )}
        </span>
      )}
      <Modal isOpen={loginModalOpen} className="login-auth-modal">
        <ModalHeader toggle={() => setLoginModalOpen(false)}>Login</ModalHeader>
        <ModalBody className="login-auth-modal-body">
          <div className="login-auth-intro">
            <div className="login-auth-intro-title">
              Unlock your gradebook and connected workflows
            </div>
            <p className="mb-2">{loginUnlockSummary}</p>
          </div>
          <Formik
            initialValues={{ email: "", password: "" }}
            onSubmit={handleLogin}
            validate={validateUserLoginForm}
          >
            {(formik) => (
              <Form className="login-auth-form">
                {loginError && (
                  <p className="text-danger">{loginError}</p>
                )}
                <FormGroup>
                  <Label htmlFor="email" className="login-auth-label">
                    Email
                    <Field
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Email address"
                      className="form-control"
                    />
                    <ErrorMessage name="email">
                      {(msg) => <p className="text-danger">{msg}</p>}
                    </ErrorMessage>
                  </Label>
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="password" className="login-auth-label">
                    Password
                    <Field
                      id="password"
                      name="password"
                      placeholder="Password"
                      className="form-control"
                    />
                    <ErrorMessage name="password">
                      {(msg) => <p className="text-danger">{msg}</p>}
                    </ErrorMessage>
                  </Label>
                </FormGroup>
                <Button type="submit" color="primary" className="login-submit-btn">
                  Login
                </Button>
                &nbsp;&nbsp;&nbsp;
                <Link
                  to="/mydata"
                  className="login-create-account-link"
                  type="submit"
                  color="primary"
                  onClick={() => setLoginModalOpen(false)}
                >
                  Create Account
                </Link>
              </Form>
            )}
          </Formik>
          <div className="d-flex justify-content-center mt-3">
            <FormGroup check>
              <Label
                check
                className="login-analytics-label"
                style={{ fontSize: "0.85rem" }}
              >
                <Input
                  type="checkbox"
                  checked={analyticsConsent}
                  onChange={(event) => setAnalyticsConsent(event.target.checked)}
                />{" "}
                Help Improve Teach League by sharing anonymized usage data.
              </Label>
            </FormGroup>
          </div>
          <div className="text-center my-3 login-auth-divider">
            <hr />
            <small className="text-muted">or</small>
            <hr />
          </div>
          <Button
            color="light"
            className="w-100 d-flex align-items-center justify-content-center login-google-btn"
            onClick={handleGoogleLogin}
            disabled={isGoogleSignInStarting}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" style={{ marginRight: '8px' }}>
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z"/>
            </svg>
            {isGoogleSignInStarting ? "Starting Google sign-in..." : "Sign in with Google"}
          </Button>
          {showDriveAvailabilityNote ? (
            <small className="text-muted d-block mt-2">
              Drive access is optional. Connect it later from your Account page after signing in.
            </small>
          ) : null}
          <div className="login-auth-legal">
            <small className="text-muted d-block">
              By using Teach League, you agree to the Terms of Service and can
              review how data is handled in the Privacy Policy.
            </small>
            <LegalLinks className="login-auth-legal-links justify-content-center" />
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default UserLoginForm;
